import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import api from '../api';

interface SiteConfigContextValue {
  auctionsPublicEnabled: boolean;
  oracleClaimsEnabled: boolean;
  oracleMaxClaimsPerStream: number;
  loading: boolean;
  refresh: () => Promise<void>;
}

const SiteConfigContext = createContext<SiteConfigContextValue | undefined>(undefined);

export const SiteConfigProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [auctionsPublicEnabled, setAuctionsPublicEnabled] = useState(false);
  const [oracleClaimsEnabled, setOracleClaimsEnabled] = useState(false);
  const [oracleMaxClaimsPerStream, setOracleMaxClaimsPerStream] = useState(3);
  const [loading, setLoading] = useState(true);

  const refresh = async () => {
    try {
      const { data } = await api.get<{
        auctionsPublicEnabled: boolean;
        oracleClaimsEnabled?: boolean;
        oracleMaxClaimsPerStream?: number;
      }>('/config/public');
      setAuctionsPublicEnabled(data.auctionsPublicEnabled === true);
      setOracleClaimsEnabled(data.oracleClaimsEnabled === true);
      setOracleMaxClaimsPerStream(data.oracleMaxClaimsPerStream || 3);
    } catch {
      setAuctionsPublicEnabled(false);
      setOracleClaimsEnabled(false);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refresh();
    const id = window.setInterval(() => {
      refresh();
    }, 20000);
    return () => window.clearInterval(id);
  }, []);

  const value = useMemo(
    () => ({
      auctionsPublicEnabled,
      oracleClaimsEnabled,
      oracleMaxClaimsPerStream,
      loading,
      refresh,
    }),
    [auctionsPublicEnabled, oracleClaimsEnabled, oracleMaxClaimsPerStream, loading],
  );

  return (
    <SiteConfigContext.Provider value={value}>{children}</SiteConfigContext.Provider>
  );
};

export function useSiteConfig() {
  const ctx = useContext(SiteConfigContext);
  if (!ctx) throw new Error('useSiteConfig debe usarse dentro de SiteConfigProvider');
  return ctx;
}
